import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { PasswordInput } from "@/components/PasswordInput";
import { Icon } from "@/components/Icon";
import { Tabs } from "@/components/Tabs";
import { authApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/Input";
import {
    startRegistration,
    startAuthentication,
} from "@simplewebauthn/browser";
import { useEffect } from "react";
import { Modal } from "@/components/Modal";
import { formatDate } from "@/utils/format";

export const SettingsSecurityTab: React.FC = () => {
    const { t } = useTranslation("global");
    const { showToast } = useToast();
    const { user, setUser } = useAuth();

    const { subtab } = useParams<{ subtab?: string }>();
    const navigate = useNavigate();

    // Tabs State
    const activeTab =
        subtab === "2fa" || subtab === "biometrics" ? subtab : "password";

    const handleTabChange = (newTab: string) => {
        navigate(`/settings/security/${newTab}`);
    };

    // Password Form State
    const [currentPassword, setCurrentPassword] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [confirmPassword, setConfirmPassword] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);

    // 2FA State
    const is2faEnabled = !!user?.two_factor_confirmed_at;
    const [qrCodeSvg, setQrCodeSvg] = useState<string | null>(null);
    const [twoFactorSecret, setTwoFactorSecret] = useState<string | null>(null);
    const [twoFactorCode, setTwoFactorCode] = useState<string>("");
    const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
    const [loading2fa, setLoading2fa] = useState<boolean>(false);

    // Biometrics State
    const [loadingBiometrics, setLoadingBiometrics] = useState<boolean>(false);
    const [biometricsList, setBiometricsList] = useState<any[]>([]);
    const [credentialToDelete, setCredentialToDelete] = useState<string | null>(
        null
    );

    const [identifying, setIdentifying] = useState<boolean>(false);
    const [identifiedId, setIdentifiedId] = useState<string | null>(null);

    useEffect(() => {
        if (activeTab === "biometrics") {
            loadBiometrics();
        }
    }, [activeTab]);

    const identifyBiometric = async () => {
        setIdentifying(true);
        setIdentifiedId(null);
        try {
            const options = await authApi.webauthnLoginOptions({
                email: user?.email,
            });
            const asseResp = await startAuthentication({
                optionsJSON: options,
            });

            const credentialId = asseResp.id;
            setIdentifiedId(credentialId);
            showToast(
                t("settings.security.biometrics.identified_success"),
                "success"
            );

            setTimeout(() => {
                setIdentifiedId(null);
            }, 4000);
        } catch (error: any) {
            if (
                error.name === "NotAllowedError" ||
                (error.message && error.message.includes("not allowed"))
            ) {
                showToast(t("settings.security.biometrics.cancelled"), "error");
            } else {
                showToast(
                    t("settings.security.biometrics.identify_error"),
                    "error"
                );
            }
        } finally {
            setIdentifying(false);
        }
    };

    const loadBiometrics = async () => {
        try {
            const data = await authApi.webauthnCredentials();
            setBiometricsList(data || []);
        } catch (error) {
            console.error("Failed to load biometrics:", error);
        }
    };

    const handleDeleteBiometric = async () => {
        if (!credentialToDelete) return;
        try {
            await authApi.deleteWebauthnCredential(credentialToDelete);
            showToast(
                t("settings.security.biometrics.delete_success"),
                "success"
            );
            loadBiometrics();
        } catch (error: any) {
            showToast(
                error.response?.data?.message || t("errors.server"),
                "error"
            );
        } finally {
            setCredentialToDelete(null);
        }
    };

    const handlePasswordSubmit = async (
        e: React.FormEvent<HTMLFormElement>
    ): Promise<void> => {
        e.preventDefault();

        if (!currentPassword || !newPassword || !confirmPassword) {
            showToast(t("settings.security.password_required"), "error");
            return;
        }

        if (newPassword !== confirmPassword) {
            showToast(t("settings.security.password_mismatch"), "error");
            return;
        }

        try {
            setLoading(true);
            await authApi.updatePassword({
                current_password: currentPassword,
                password: newPassword,
                password_confirmation: confirmPassword,
            });
            showToast(t("settings.security.password_updated"), "success");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (error: any) {
            const message =
                error.response?.data?.message ||
                t("settings.security.password_update_error");
            showToast(message, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleEnable2fa = async () => {
        try {
            setLoading2fa(true);
            const res = await authApi.enable2fa();
            setQrCodeSvg(res.qr_code_svg);
            setTwoFactorSecret(res.secret);
        } catch (error: any) {
            showToast(
                error.message || t("settings.security.two_factor.error_enable"),
                "error"
            );
        } finally {
            setLoading2fa(false);
        }
    };

    const handleConfirm2fa = async () => {
        try {
            setLoading2fa(true);
            const res = await authApi.confirm2fa({ code: twoFactorCode });
            setRecoveryCodes(res.recovery_codes);
            showToast(res.message, "success");
            // Update local user state so UI knows it's enabled
            setUser({
                ...user,
                two_factor_confirmed_at: new Date().toISOString(),
            });
            setQrCodeSvg(null);
            setTwoFactorSecret(null);
            setTwoFactorCode("");
        } catch (error: any) {
            showToast(
                error.message || t("settings.security.two_factor.error_code"),
                "error"
            );
        } finally {
            setLoading2fa(false);
        }
    };

    const handleDownloadRecoveryCodes = () => {
        if (!recoveryCodes) return;
        const blob = new Blob(
            [JSON.stringify({ recovery_codes: recoveryCodes }, null, 2)],
            { type: "application/json" }
        );
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "vigepra2_recovery_codes.json";
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleDisable2fa = async () => {
        try {
            setLoading2fa(true);
            const res = await authApi.disable2fa();
            showToast(res.message, "success");
            setUser({
                ...user,
                two_factor_confirmed_at: null,
                two_factor_secret: null,
                two_factor_recovery_codes: null,
            });
            setRecoveryCodes(null);
        } catch (error: any) {
            showToast(
                error.message ||
                    t("settings.security.two_factor.error_disable"),
                "error"
            );
        } finally {
            setLoading2fa(false);
        }
    };

    const handleRegisterBiometric = async () => {
        setLoadingBiometrics(true);
        try {
            const options = await authApi.webauthnRegisterOptions();
            const attResp = await startRegistration({ optionsJSON: options });
            await authApi.webauthnRegister(attResp);
            showToast(t("settings.security.biometrics.success"), "success");
            loadBiometrics();
        } catch (err: any) {
            console.error("WebAuthn Error:", err);
            let msg = t("settings.security.biometrics.error");
            if (
                err.name === "NotAllowedError" ||
                (err.message && err.message.includes("not allowed"))
            ) {
                msg = t("settings.security.biometrics.cancelled");
            } else if (err instanceof Error) {
                if (
                    err.message ===
                    "The authenticator was previously registered"
                ) {
                    msg = t("settings.security.biometrics.already_registered");
                } else {
                    msg = err.message;
                }
            }
            showToast(msg, "error");
        } finally {
            setLoadingBiometrics(false);
        }
    };

    const getTabHeader = () => {
        switch (activeTab) {
            case "2fa":
                return {
                    title: t("settings.security.two_factor.title"),
                    subtitle: t("settings.security.two_factor.description"),
                };
            case "biometrics":
                return {
                    title: t("settings.security.biometrics.title"),
                    subtitle: t("settings.security.biometrics.description"),
                };
            default:
                return {
                    title: t("settings.security.title"),
                    subtitle: t("settings.security.subtitle"),
                };
        }
    };

    const header = getTabHeader();

    return (
        <div className="max-w-4xl mx-auto w-full min-w-0">
            <div className="box animate-fade-in">
                {/* Subtabs Navigation */}
                <Tabs
                    tabs={[
                        {
                            id: "password",
                            label: t("settings.security.tabs.password"),
                            icon: <Icon value="key" size={16} />,
                        },
                        {
                            id: "2fa",
                            label: t("settings.security.tabs.two_factor"),
                            icon: <Icon value="shieldCheck" size={16} />,
                        },
                        {
                            id: "biometrics",
                            label: t("settings.security.tabs.biometrics"),
                            icon: <Icon value="fingerprint" size={16} />,
                        },
                    ]}
                    activeTab={activeTab}
                    onChange={handleTabChange}
                    className="px-6 pt-2 sm:px-8"
                    subtabs
                    full
                    syncUrl={false}
                />

                <div className="p-6 sm:p-8">
                    <div className="text-center mb-6">
                        <h3 className="box-title text-xl">{header.title}</h3>
                        <p className="box-subtitle mt-1">{header.subtitle}</p>
                    </div>

                    {/* PASSWORD TAB */}
                    {activeTab === "password" && (
                        <div className="max-w-xl mx-auto pt-4">
                            <form
                                onSubmit={handlePasswordSubmit}
                                className="space-y-4 w-full"
                            >
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                                        {t(
                                            "settings.security.current_password"
                                        )}
                                    </label>
                                    <PasswordInput
                                        value={currentPassword}
                                        onChange={(e) =>
                                            setCurrentPassword(e.target.value)
                                        }
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                                        {t("settings.security.new_password")}
                                    </label>
                                    <PasswordInput
                                        value={newPassword}
                                        onChange={(e) =>
                                            setNewPassword(e.target.value)
                                        }
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                                        {t(
                                            "settings.security.confirm_password"
                                        )}
                                    </label>
                                    <PasswordInput
                                        value={confirmPassword}
                                        onChange={(e) =>
                                            setConfirmPassword(e.target.value)
                                        }
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div className="pt-2 flex justify-center">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading}
                                        className="flex items-center gap-2"
                                    >
                                        {loading && (
                                            <Icon
                                                value="loader2"
                                                className="animate-spin"
                                                size={16}
                                            />
                                        )}
                                        {t("settings.security.save_password")}
                                    </Button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* 2FA TAB */}
                    {activeTab === "2fa" && (
                        <div className="flex flex-col items-center py-4 text-center animate-fade-in w-full">
                            {is2faEnabled ? (
                                <div className="w-full max-w-md">
                                    <Icon
                                        value="shieldCheck"
                                        size={64}
                                        className="mx-auto text-emerald-500"
                                    />
                                    <h4 className="text-lg font-bold text-slate-800 mt-2">
                                        {t(
                                            "settings.security.two_factor.enabled_title"
                                        )}
                                    </h4>
                                    <p className="text-sm text-slate-500">
                                        {t(
                                            "settings.security.two_factor.enabled_desc"
                                        )}
                                    </p>

                                    {recoveryCodes && (
                                        <div className="animate-recovery-pulse mt-6 p-4 rounded-lg border-2 text-center">
                                            <h5 className="font-bold text-sm mb-2 text-slate-700 dark:text-zinc-200">
                                                {t(
                                                    "settings.security.two_factor.recovery_title"
                                                )}
                                            </h5>
                                            <p className="text-xs text-slate-500 mb-4">
                                                {t(
                                                    "settings.security.two_factor.recovery_desc"
                                                )}
                                            </p>
                                            <div className="grid grid-cols-2 gap-2 font-mono text-xs text-slate-700 dark:text-zinc-300">
                                                {recoveryCodes.map(
                                                    (code, idx) => (
                                                        <div
                                                            key={idx}
                                                            className="bg-white dark:bg-zinc-900 px-2 py-1 rounded border border-slate-200 dark:border-zinc-800 text-center"
                                                        >
                                                            {code}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                            <div className="mt-4 flex justify-center">
                                                <Button
                                                    variant="secondary"
                                                    className="animate-btn-primary-pulse flex items-center gap-2 text-sm"
                                                    onClick={
                                                        handleDownloadRecoveryCodes
                                                    }
                                                >
                                                    {t(
                                                        "settings.security.two_factor.download_codes"
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    )}

                                    <Button
                                        variant="danger"
                                        className="mt-8"
                                        onClick={handleDisable2fa}
                                        disabled={loading2fa}
                                    >
                                        {loading2fa ? (
                                            <Icon
                                                value="loader2"
                                                className="animate-spin"
                                                size={16}
                                            />
                                        ) : (
                                            t(
                                                "settings.security.two_factor.disable_btn"
                                            )
                                        )}
                                    </Button>
                                </div>
                            ) : qrCodeSvg ? (
                                <div className="w-full max-w-sm mx-auto text-left">
                                    <p className="text-sm text-slate-600 mb-4 text-center">
                                        {t(
                                            "settings.security.two_factor.scan_desc"
                                        )}
                                    </p>
                                    <div
                                        className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex justify-center mb-4"
                                        dangerouslySetInnerHTML={{
                                            __html: qrCodeSvg,
                                        }}
                                    />
                                    <p className="text-xs text-center text-slate-500 mb-6 font-mono bg-slate-50 p-2 rounded">
                                        {twoFactorSecret}
                                    </p>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                                                {t(
                                                    "settings.security.two_factor.code_label"
                                                )}
                                            </label>
                                            <Input
                                                value={twoFactorCode}
                                                onChange={(e) =>
                                                    setTwoFactorCode(
                                                        e.target.value
                                                    )
                                                }
                                                placeholder={t(
                                                    "settings.security.two_factor.code_placeholder"
                                                )}
                                            />
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="secondary"
                                                className="flex-1"
                                                onClick={() =>
                                                    setQrCodeSvg(null)
                                                }
                                            >
                                                {t(
                                                    "settings.security.two_factor.cancel"
                                                )}
                                            </Button>
                                            <Button
                                                variant="primary"
                                                className="flex-1 flex justify-center items-center gap-2"
                                                onClick={handleConfirm2fa}
                                                disabled={
                                                    loading2fa ||
                                                    twoFactorCode.length < 6
                                                }
                                            >
                                                {loading2fa && (
                                                    <Icon
                                                        value="loader2"
                                                        className="animate-spin"
                                                        size={16}
                                                    />
                                                )}
                                                {t(
                                                    "settings.security.two_factor.confirm"
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="max-w-sm mx-auto">
                                    <Icon
                                        value="shieldAlert"
                                        size={48}
                                        className="mx-auto text-slate-300"
                                    />
                                    <Button
                                        variant="primary"
                                        className="mt-4 w-full flex justify-center items-center gap-2"
                                        onClick={handleEnable2fa}
                                        disabled={loading2fa}
                                    >
                                        {loading2fa && (
                                            <Icon
                                                value="loader2"
                                                className="animate-spin"
                                                size={16}
                                            />
                                        )}
                                        {t(
                                            "settings.security.two_factor.button"
                                        )}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* BIOMETRICS TAB */}
                    {activeTab === "biometrics" && (
                        <div className="flex flex-col items-center py-4 animate-fade-in">
                            <Icon
                                value="scanFace"
                                size={48}
                                className="mx-auto text-slate-300"
                            />

                            <p className="mt-4 text-sm text-slate-600 dark:text-slate-400 text-center max-w-md">
                                {t("settings.security.biometrics.description")}
                            </p>

                            {biometricsList.length > 0 && (
                                <Button
                                    variant="secondary"
                                    className="mt-4 flex items-center text-sm"
                                    onClick={identifyBiometric}
                                    disabled={identifying}
                                >
                                    {identifying && (
                                        <Icon
                                            value="loader2"
                                            className="animate-spin mr-1"
                                            size={16}
                                        />
                                    )}
                                    {!identifying && (
                                        <Icon
                                            value="scanFace"
                                            className="mr-1"
                                            size={16}
                                        />
                                    )}
                                    {t("settings.security.biometrics.identify")}
                                </Button>
                            )}

                            <div className="w-full max-w-md mt-6 text-left space-y-3">
                                {biometricsList.map((credential) => (
                                    <div
                                        key={credential.id}
                                        className={`flex items-center justify-between p-3 border rounded-xl transition-all duration-500 ${identifiedId === credential.id ? "border-primary bg-primary/10 shadow-lg scale-[1.02] animate-[pulse_0.5s_cubic-bezier(0.4,0,0.6,1)_infinite]" : "border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-900/50"}`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                                <Icon
                                                    value="fingerprint"
                                                    size={20}
                                                    className="text-primary"
                                                />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-semibold text-sm truncate dark:text-white">
                                                    {credential.alias ||
                                                        t(
                                                            "settings.security.biometrics.device_subtitle"
                                                        )}
                                                </div>
                                                <div className="text-xs text-slate-500 truncate">
                                                    {credential.created_at
                                                        ? t(
                                                              "settings.security.biometrics.registered_on",
                                                              {
                                                                  date: formatDate(
                                                                      credential.created_at
                                                                  ),
                                                              }
                                                          )
                                                        : t(
                                                              "settings.security.biometrics.linked_to_account"
                                                          )}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setCredentialToDelete(
                                                    credential.id
                                                )
                                            }
                                            className="shrink-0 p-2 text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                                            title={t(
                                                "settings.security.biometrics.delete"
                                            )}
                                        >
                                            <Icon value="trash" size={18} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <Button
                                variant="primary"
                                className="mt-4 flex items-center gap-2"
                                onClick={handleRegisterBiometric}
                                disabled={loadingBiometrics}
                            >
                                {loadingBiometrics && (
                                    <Icon
                                        value="loader2"
                                        className="animate-spin"
                                        size={16}
                                    />
                                )}
                                {t("settings.security.biometrics.button")}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <Modal
                isOpen={!!credentialToDelete}
                onClose={() => setCredentialToDelete(null)}
                title={t("settings.security.biometrics.delete")}
                submitVariant="danger"
                submitLabel={t("settings.security.biometrics.delete")}
                onSubmit={handleDeleteBiometric}
                isSubmitting={loadingBiometrics}
            >
                <div className="text-center text-slate-600 dark:text-slate-400">
                    <p>{t("settings.security.biometrics.delete_confirm")}</p>
                    {credentialToDelete && (
                        <p className="font-bold flex justify-center items-center gap-2 mt-4">
                            <Icon
                                value="fingerprint"
                                size={24}
                                className="text-slate-400"
                            />
                            {biometricsList.find(
                                (c) => c.id === credentialToDelete
                            )?.alias ||
                                t(
                                    "settings.security.biometrics.generic_device"
                                )}
                        </p>
                    )}
                </div>
            </Modal>
        </div>
    );
};
